import React, { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import useApiGet from "../hooks/getData";
import useApiPost from "../hooks/postData";
import RoomPicker from "./RoomPicker";
import {
  StatusBadge,
  formatCountdown,
  formatDateTimeLocal,
  formatDuration,
} from "./tournamentUtils";

const POLL_MS = 5000;
const errMsg = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * Detail / live view of one tournament.
 * - polls GET tournaments/{id} every 5s while it isn't finished
 * - countdown uses server_time so the admin's clock skew doesn't matter
 * - scheduled tournaments: add / remove rooms, start now
 * - live / scheduled: cancel
 */
function TournamentDetail({ tournamentId, onClose, onChanged }) {
  const { getData } = useApiGet();
  const { postData, loading: saving } = useApiPost();

  const [tournament, setTournament] = useState(null);
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [addOpen, setAddOpen] = useState(false);
  const [newRoomIds, setNewRoomIds] = useState([]);
  const [confirmCancel, setConfirmCancel] = useState(false);

  // serverTime - clientTime, so countdown follows the server clock.
  const skewRef = useRef(0);
  const lastStatusRef = useRef(null);

  const load = useCallback(
    async (silent = false) => {
      if (!tournamentId) return;
      if (!silent) setLoading(true);
      try {
        const res = await getData(`/tournaments/${tournamentId}`);
        const t = res?.data?.tournament;
        if (t) {
          const st = res?.data?.server_time || t.server_time;
          if (st) skewRef.current = new Date(st).getTime() - Date.now();
          setTournament(t);
          if (lastStatusRef.current && lastStatusRef.current !== t.status) onChanged?.();
          lastStatusRef.current = t.status;
        }
      } catch (err) {
        if (!silent) toast.error(errMsg(err, "Failed to load tournament."));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    // eslint-disable-next-line
    [tournamentId]
  );

  const loadSupporters = useCallback(async () => {
    if (!tournamentId) return;
    try {
      const res = await getData(`/tournaments/${tournamentId}/supporters`);
      setSupporters(res?.data?.supporters || []);
    } catch (err) {
      // supporters are optional, ignore
    }
    // eslint-disable-next-line
  }, [tournamentId]);

  // reset when a different tournament is opened / dialog closed
  useEffect(() => {
    setTournament(null);
    setSupporters([]);
    setAddOpen(false);
    setNewRoomIds([]);
    setConfirmCancel(false);
    lastStatusRef.current = null;
    if (tournamentId) {
      load();
      loadSupporters();
    }
  }, [tournamentId, load, loadSupporters]);

  const status = tournament?.status;
  const active = status === "scheduled" || status === "live" || status === "settling";
  const scoreSeq = tournament?.score_seq;

  // polling
  useEffect(() => {
    if (!tournamentId || !active) return;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [tournamentId, active, load]);

  // refresh supporters whenever the score changes (and once at the end)
  useEffect(() => {
    if (tournamentId && scoreSeq !== undefined) loadSupporters();
  }, [tournamentId, scoreSeq, status, loadSupporters]);

  // 1s ticker for the countdown
  useEffect(() => {
    if (!tournamentId || !active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [tournamentId, active]);

  const serverNow = now + skewRef.current;

  const countdown = () => {
    if (!tournament) return null;
    if (status === "scheduled") {
      return `Starts in ${formatCountdown(new Date(tournament.starts_at).getTime() - serverNow)}`;
    }
    if (status === "live") {
      return `Ends in ${formatCountdown(new Date(tournament.ends_at).getTime() - serverNow)}`;
    }
    if (status === "settling") return "Calculating result...";
    return null;
  };

  const act = async (endpoint, body, okFallback) => {
    try {
      const res = await postData(endpoint, body);
      if (res?.success) {
        toast.success(res.message || okFallback);
        if (res?.data?.tournament) setTournament(res.data.tournament);
        else load(true);
        onChanged?.();
        return true;
      }
      toast.error(res?.message || "Something went wrong.");
    } catch (err) {
      toast.error(errMsg(err, "Request failed."));
      load(true);
      onChanged?.();
    }
    return false;
  };

  const handleStart = () => act(`/tournaments/admin/${tournamentId}/start`, {}, "Tournament started.");

  const handleCancel = async () => {
    await act(`/tournaments/admin/${tournamentId}/cancel`, {}, "Tournament cancelled.");
    setConfirmCancel(false);
  };

  const handleRemoveRoom = (roomId) =>
    act(`/tournaments/admin/${tournamentId}/rooms/remove`, { room_id: roomId }, "Room removed.");

  const handleAddRooms = async () => {
    if (newRoomIds.length === 0) return toast.error("Select at least one room.");
    const ok = await act(
      `/tournaments/admin/${tournamentId}/rooms/add`,
      { room_ids: newRoomIds },
      "Rooms added."
    );
    if (ok) {
      setAddOpen(false);
      setNewRoomIds([]);
    }
  };

  const participants = tournament?.participants || [];
  // After the 30-day reset the live total is 0; show the saved pre-reset value instead.
  const shownCoins = (p) => p.coins_before_reset ?? p.total_coins;
  const maxCoins = Math.max(1, ...participants.map(shownCoins));
  const roomNameById = Object.fromEntries(participants.map((p) => [p.room_id, p.room_name]));

  return (
    <>
      <Dialog open={!!tournamentId} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <div className="flex items-center justify-between gap-3">
            <span>{tournament?.title || "Tournament"}</span>
            {tournament && <StatusBadge status={tournament.status} />}
          </div>
        </DialogTitle>
        <DialogContent>
          {loading && !tournament ? (
            <p className="py-6 font-poppins dark:text-darkText">Loading...</p>
          ) : !tournament ? (
            <p className="py-6 font-poppins dark:text-darkText">Tournament not found.</p>
          ) : (
            <div className="flex flex-col gap-4 py-2 font-poppins dark:text-darkText">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                <Info label="Duration" value={formatDuration(tournament.duration_sec)} />
                <Info label="Starts" value={formatDateTimeLocal(tournament.starts_at)} />
                <Info label="Ends" value={formatDateTimeLocal(tournament.ends_at)} />
                <Info
                  label="Result"
                  value={tournament.result ? tournament.result.replace("_", " ") : "-"}
                />
              </div>

              {countdown() && (
                <div className="px-4 py-3 text-center text-lg font-semibold rounded-lg bg-[#593A99] bg-opacity-10 text-[#593A99] dark:text-darkText">
                  {countdown()}
                </div>
              )}

              {tournament.coins_reset_at && (
                <div className="px-4 py-3 text-sm rounded-lg bg-[#F5B301] bg-opacity-15">
                  Group coins were reset to 0 on {formatDateTimeLocal(tournament.coins_reset_at)}. The coins
                  shown below are the saved values from before the reset.
                </div>
              )}

              {tournament.status === "cancelled" && tournament.cancel_reason && (
                <div className="px-4 py-3 text-sm rounded-lg text-[#D92D20] bg-[#D92D20] bg-opacity-10">
                  {tournament.cancel_reason}
                </div>
              )}

              {/* Standings */}
              <div>
                <div className="flex items-center justify-between pb-2">
                  <h3 className="font-semibold">Groups ({participants.length})</h3>
                  {status === "scheduled" && (
                    <button
                      onClick={() => setAddOpen(true)}
                      className="px-3 py-1.5 text-xs text-[#593A99] border border-[#593A99] rounded-lg"
                    >
                      + Add Rooms
                    </button>
                  )}
                </div>

                <div className="overflow-x-auto border border-opacity-gradient dark:border-[#1F1F1F] rounded-lg">
                  <table className="min-w-full text-sm text-left">
                    <thead style={{ backgroundColor: "#593A99" }} className="text-white">
                      <tr>
                        <th className="p-3">Rank</th>
                        <th className="p-3">Room</th>
                        <th className="p-3">Host</th>
                        <th className="p-3">Status</th>
                        <th className="p-3">Coins</th>
                        {status === "scheduled" && <th className="p-3">Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {participants.map((p) => (
                        <tr
                          key={p.room_id}
                          className={`border-t dark:border-[#1F1F1F] ${
                            p.is_winner ? "bg-[#F5B301] bg-opacity-15" : ""
                          }`}
                        >
                          <td className="p-3">{p.rank ? `#${p.rank}` : "-"}</td>
                          <td className="p-3">
                            {p.room_name || "-"}
                            <div className="text-xs text-[#666666] dark:text-darkText">ID {p.room_id}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {p.host_avatar ? (
                                <img
                                  src={p.host_avatar}
                                  alt=""
                                  className="object-cover w-7 h-7 rounded-full"
                                  onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                                />
                              ) : null}
                              {p.host_name || p.host_user_id}
                            </div>
                          </td>
                          <td className="p-3 capitalize">
                            {p.status.replace("_", " ")}
                            {p.is_winner && (
                              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-[#F5B301] text-black">
                                Winner
                              </span>
                            )}
                          </td>
                          <td className="p-3 min-w-[140px]">
                            <div className="font-semibold">{shownCoins(p).toLocaleString()}</div>
                            <div className="w-full h-1.5 mt-1 rounded bg-[#D5CCE8] bg-opacity-50">
                              <div
                                className="h-1.5 rounded bg-[#593A99]"
                                style={{ width: `${(shownCoins(p) / maxCoins) * 100}%` }}
                              />
                            </div>
                          </td>
                          {status === "scheduled" && (
                            <td className="p-3">
                              <button
                                onClick={() => handleRemoveRoom(p.room_id)}
                                disabled={saving}
                                className="px-3 py-1.5 text-xs text-[#D92D20] border border-[#D92D20] rounded-lg disabled:opacity-40"
                              >
                                Remove
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {status === "scheduled" && participants.length <= 2 && (
                  <p className="pt-1 text-xs text-[#D92D20]">
                    A tournament needs at least 2 groups online at start time, or it is cancelled.
                  </p>
                )}
              </div>

              {/* Supporters */}
              <div>
                <h3 className="pb-2 font-semibold">Top Supporters</h3>
                {supporters.length === 0 ? (
                  <p className="text-sm text-[#666666] dark:text-darkText">No gifts yet.</p>
                ) : (
                  <div className="overflow-y-auto border max-h-56 border-opacity-gradient dark:border-[#1F1F1F] rounded-lg">
                    {supporters.map((s, i) => (
                      <div
                        key={`${s.user_id}-${s.room_id}`}
                        className="flex items-center gap-3 p-2.5 text-sm border-b last:border-b-0 dark:border-[#1F1F1F]"
                      >
                        <span className="w-6 text-[#666666] dark:text-darkText">{i + 1}</span>
                        {s.avatar ? (
                          <img
                            src={s.avatar}
                            alt=""
                            className="object-cover w-7 h-7 rounded-full"
                            onError={(e) => (e.currentTarget.style.visibility = "hidden")}
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-[#D5CCE8]" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{s.name || s.user_id}</div>
                          <div className="text-xs text-[#666666] dark:text-darkText truncate">
                            for {roomNameById[s.room_id] || `Room ${s.room_id}`}
                          </div>
                        </div>
                        <span className="font-semibold">{s.total_coins.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={onClose}
                  className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg"
                >
                  Close
                </button>
                {status === "scheduled" && (
                  <button
                    onClick={handleStart}
                    disabled={saving}
                    style={{ backgroundColor: "#0D9947" }}
                    className="px-6 py-2.5 text-sm font-medium text-white rounded-lg disabled:opacity-60"
                  >
                    Start Now
                  </button>
                )}
                {(status === "scheduled" || status === "live") && (
                  <button
                    onClick={() => setConfirmCancel(true)}
                    style={{ backgroundColor: "#D92D20" }}
                    className="px-6 py-2.5 text-sm font-medium text-white rounded-lg"
                  >
                    Cancel Tournament
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add rooms */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Add Rooms</DialogTitle>
        <DialogContent>
          <RoomPicker
            selected={newRoomIds}
            onChange={setNewRoomIds}
            excludeRoomIds={participants.map((p) => p.room_id)}
          />
          <div className="flex justify-end gap-2 pt-4">
            <button
              onClick={() => setAddOpen(false)}
              className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
            >
              Cancel
            </button>
            <button
              onClick={handleAddRooms}
              disabled={saving}
              style={{ backgroundColor: "#593A99" }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins disabled:opacity-60"
            >
              {saving ? "Adding..." : `Add (${newRoomIds.length})`}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel confirm */}
      <Dialog open={confirmCancel} onClose={() => setConfirmCancel(false)}>
        <DialogTitle>Cancel Tournament?</DialogTitle>
        <DialogContent>
          <p className="pb-4 font-poppins dark:text-darkText">
            The tournament will end immediately. Coins already spent on gifts are not refunded.
          </p>
          <div className="flex justify-end gap-2 pb-2">
            <button
              onClick={() => setConfirmCancel(false)}
              className="px-6 py-2.5 text-sm font-medium border border-header rounded-lg font-poppins dark:text-darkText"
            >
              Keep
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              style={{ backgroundColor: "#D92D20" }}
              className="px-6 py-2.5 text-sm font-medium text-white rounded-lg font-poppins disabled:opacity-60"
            >
              Cancel Tournament
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs text-[#666666] dark:text-darkText">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

export default TournamentDetail;
